import { BufferManager } from './buffer-manager.ts'
import type { SharedBuffers } from './buffer-manager.ts'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { ControlIndex, hashFloat32Array, MAX_SAMPLE_LENGTH,
  type SequenceData as BytecodeSequenceData } from './bytecode.ts'
import { ImpulseResponseLoader } from './impulse-response-loader.ts'
import { rpc } from './lib/rpc.ts'
import { RingAnalyser } from './ring-analyser.ts'
import { SamLoader } from './sam-loader.ts'
import { SampleLoader } from './sample-loader.ts'

interface SampleData {
  sampleId: number
  data: ArrayBuffer
  length: number
  contentHash: number
}

interface IRData {
  irId: number
  data: ArrayBuffer
  length: number
}

interface SequenceData {
  data: Float64Array
  slotIndex: number
}

interface SingleProgramData {
  bytecode: Float64Array
  sequences: { data: Float64Array; slotIndex: number }[]
  samples: SampleData[]
  impulseResponses: IRData[]
  bpm: number
  hasExplicitBpm: boolean
  tune: number
}

interface DualProgramData {
  programA: SingleProgramData
  programB: SingleProgramData
  globalBpm: number
  crossfade: number
  tune: number
}

interface WorkletRpc {
  init(buffers: SharedBuffers): Promise<{ wasmMemory?: SharedArrayBuffer }>
  loadWasm(binary: ArrayBuffer): Promise<{ wasmMemory: SharedArrayBuffer }>
  loadProgram(data: SingleProgramData | DualProgramData): Promise<void>
  loadNewProgram(data: SingleProgramData | DualProgramData): Promise<void>
  getSequencerPointers(): Promise<Array<{ seqId: number; statePtr: number; sequenceId: number }>>
  getWaveformPointers(): Promise<Array<{ waveformId: number; bufferPtr: number; writePosition: number }>>
  loadIR(irId: number, irData: ArrayBuffer, length: number): Promise<void>
  loadSample(sampleId: number, sampleData: ArrayBuffer, length: number, contentHash: number): Promise<void>
  start(): Promise<void>
  stop(): Promise<void>
}

export class loopmaster {
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private workletRpc: WorkletRpc | null = null
  private wasmMemory: SharedArrayBuffer | null = null
  private masterGain: GainNode | null = null
  private bufferManager: BufferManager
  private compiler: BytecodeCompiler
  private irLoader: ImpulseResponseLoader | null = null
  private sampleLoader: SampleLoader | null = null
  private samLoader: SamLoader | null = null
  private initialized = false
  private globalBpm = 120
  private tune = 1.0
  private crossfade = 0.5
  private masterVolume = 1
  private oversampling: 1 | 2 | 4 | 8 = 1
  private isFirstLoad = true
  private ringAnalyser: RingAnalyser | null = null

  constructor() {
    this.bufferManager = new BufferManager()
    this.compiler = new BytecodeCompiler()
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    })
    return Promise.race([promise, timeoutPromise])
  }

  private serializeSequence(seq: BytecodeSequenceData): Float64Array {
    // Calculate total size needed
    let totalSize = 6 // id, notes.length, swing, offset, exponent, cycleLength
    for (const note of seq.notes) {
      totalSize += 5 + note.pitches.length // pitches.length + pitches + duration + velocity + hold + glide
    }

    const buffer = new Float64Array(totalSize)
    let offset = 0

    buffer[offset++] = seq.id
    buffer[offset++] = seq.notes.length
    buffer[offset++] = seq.swing ?? 0
    buffer[offset++] = seq.offset ?? 0
    buffer[offset++] = seq.exponent ?? 1
    buffer[offset++] = seq.cycleLength ?? 0

    for (const note of seq.notes) {
      buffer[offset++] = note.pitches.length
      for (let j = 0; j < note.pitches.length; j++) {
        buffer[offset++] = note.pitches[j]
      }
      buffer[offset++] = note.duration
      buffer[offset++] = note.velocity
      buffer[offset++] = note.hold
      buffer[offset++] = note.glide ?? 0
    }

    return buffer
  }

  private async ensureInitialized() {
    if (this.initialized) return
    throw new Error('loopmaster not initialized. Call init() first.')
  }

  async init(latencyHint: number = 1.0) {
    if (this.initialized && this.audioContext && this.workletNode && this.masterGain) {
      return
    }

    this.audioContext = new AudioContext({ sampleRate: 48000, latencyHint })
    this.irLoader = new ImpulseResponseLoader(this.audioContext)
    this.sampleLoader = new SampleLoader(this.audioContext)
    this.samLoader = new SamLoader(this.audioContext)

    // Load and register the processor
    try {
      const workletUrl = import.meta.env.PROD && (window as any).__WORKLET_PATH__
        ? (window as any).__WORKLET_PATH__
        : '/src/processor-wasm.ts'
      await this.audioContext.audioWorklet.addModule(workletUrl)
      console.log('AudioWorklet processor loaded')
    }
    catch (error) {
      console.error('Failed to load AudioWorklet processor:', error)
      throw new Error(`Failed to load audio processor: ${error}`)
    }

    // Create worklet node
    this.workletNode = new AudioWorkletNode(this.audioContext, 'dspscript-processor', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: {
        sourcemapUrl: new URL('/as/build/index.wasm.map', location.origin).toString(),
      },
    })

    // Reset to first load state since new worklet has no samples loaded
    this.isFirstLoad = true

    // Create master gain and connect to output (post-analyser tap)
    this.masterGain = this.audioContext.createGain()
    this.masterGain.gain.value = this.masterVolume
    this.workletNode.connect(this.masterGain)
    this.masterGain.connect(this.audioContext.destination)

    // Set up RPC client immediately
    this.workletRpc = rpc<WorkletRpc>(this.workletNode.port)

    // Load WASM binary
    const wasmResult = await this.loadWasm()
    if (wasmResult?.wasmMemory) {
      this.wasmMemory = wasmResult.wasmMemory
    }

    // Initialize worklet with SharedArrayBuffers
    const sharedBuffers = this.bufferManager.getSharedBuffers()
    const initResult = await this.workletRpc.init(sharedBuffers)
    this.initialized = true
    console.log('Worklet initialized with SharedArrayBuffers')
    if (initResult.wasmMemory) {
      this.wasmMemory = initResult.wasmMemory
    }

    // Create RingAnalyser for time-domain visualization
    this.ringAnalyser = new RingAnalyser(
      sharedBuffers.ringBufferLeft,
      sharedBuffers.ringBufferRight,
      sharedBuffers.ringBufferControl,
      this.audioContext.sampleRate,
    )


    // Setup HMR handler
    if (import.meta.hot) {
      import.meta.hot.on('vite:beforeUpdate', async payload => {
        if (payload.updates.some(update => update.path.includes('index.wasm'))) {
          const wasmResult = await this.loadWasm()
          if (wasmResult?.wasmMemory) {
            this.wasmMemory = wasmResult.wasmMemory
          }
        }
      })
    }
  }

  private async loadWasm(): Promise<{ wasmMemory: SharedArrayBuffer } | null> {
    if (!this.workletNode || !this.workletRpc) return null

    const response = await fetch('/as/build/index.wasm?t=' + Date.now())
    const binary = await response.arrayBuffer()
    const result = await this.workletRpc.loadWasm(binary)

    // Reset to first load state since WASM reload clears everything
    this.isFirstLoad = true

    return result
  }

  async loadDualProgram(
    sourceA: string,
    sourceB: string,
    globalBpm: number,
    crossfade: number,
  ): Promise<void> {
    this.globalBpm = globalBpm
    this.crossfade = crossfade

    await this.ensureInitialized()

    if (!this.workletRpc || !this.irLoader || !this.sampleLoader || !this.samLoader) {
      throw new Error('loopmaster not initialized. Call init() first.')
    }

    console.log('[dspscript] Loading dual mode programs')

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Loading timeout - operation took too long')), 15000)
    })

    return Promise.race([
      this.loadDualProgramInternal(sourceA, sourceB, globalBpm, crossfade, false),
      timeoutPromise,
    ])
  }

  async loadNewDualProgram(
    sourceA: string,
    sourceB: string,
    globalBpm: number,
    crossfade: number,
  ): Promise<void> {
    this.globalBpm = globalBpm
    this.crossfade = crossfade

    await this.ensureInitialized()

    if (!this.workletRpc || !this.irLoader || !this.sampleLoader || !this.samLoader) {
      throw new Error('loopmaster not initialized. Call init() first.')
    }

    console.log('[dspscript] Loading new dual mode programs (with reset)')

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Loading timeout - operation took too long')), 15000)
    })

    return Promise.race([
      this.loadDualProgramInternal(sourceA, sourceB, globalBpm, crossfade, true),
      timeoutPromise,
    ])
  }

  private async loadDualProgramInternal(
    sourceA: string,
    sourceB: string,
    globalBpm: number,
    crossfade: number,
    resetState: boolean = false,
  ): Promise<void> {
    // Compile both programs
    const programA = this.compiler.compile(sourceA, globalBpm)
    const programB = this.compiler.compile(sourceB, globalBpm)

    // Merge resources from both programs
    const allImpulseResponses = [...new Set([...programA.impulseResponses, ...programB.impulseResponses])]
    const mergedSamplePaths: string[] = []
    const samplePathSet = new Set<string>()
    for (const path of [...programA.samplePaths, ...programB.samplePaths]) {
      if (!samplePathSet.has(path)) {
        samplePathSet.add(path)
        mergedSamplePaths.push(path)
      }
    }
    const allSamSpeeches = new Map([...programA.samSpeeches, ...programB.samSpeeches])

    // Load impulse responses
    const impulseResponsesData: IRData[] = []
    for (const irPath of allImpulseResponses) {
      try {
        const irData = await this.withTimeout(
          this.irLoader!.loadImpulseResponse(irPath),
          5000,
          `IR loading timeout: ${irPath}`,
        )
        const channelData = irData.audioBuffer.getChannelData(0)
        const buffer = new Float32Array(channelData.length)
        buffer.set(channelData)
        impulseResponsesData.push({
          irId: irData.id,
          data: buffer.buffer,
          length: channelData.length,
        })
      }
      catch (error: any) {
        console.error(`Failed to load IR "${irPath}":`, error.message)
      }
    }

    // Load samples
    const samplesData: SampleData[] = []
    for (let sampleId = 0; sampleId < mergedSamplePaths.length; sampleId++) {
      const samplePath = mergedSamplePaths[sampleId]
      if (samplePath.startsWith('__SAM__')) {
        const samHashStr = samplePath.replace('__SAM__', '')
        const samHash = parseInt(samHashStr, 10)
        const params = allSamSpeeches.get(samHash)
        if (params) {
          try {
            const samData = await this.withTimeout(
              this.samLoader!.generateSpeech(params),
              5000,
              `SAM speech generation timeout: "${params.speak}"`,
            )
            const channelData = samData.audioBuffer.getChannelData(0)
            const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
            const buffer = channelData.slice(0, length)
            samplesData.push({
              sampleId,
              data: buffer.buffer,
              length,
              contentHash: samData.id,
            })
          }
          catch (error: any) {
            console.error(`Failed to generate SAM speech "${params.speak}":`, error.message)
          }
        }
      }
      else {
        try {
          const sampleData = await this.withTimeout(
            this.sampleLoader!.loadSample(samplePath),
            5000,
            `Sample loading timeout: ${samplePath}`,
          )
          const channelData = sampleData.audioBuffer.getChannelData(0)
          const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
          const buffer = channelData.slice(0, length)
          const contentHash = hashFloat32Array(buffer)
          samplesData.push({
            sampleId,
            data: buffer.buffer,
            length,
            contentHash,
          })
        }
        catch (error: any) {
          console.error(`Failed to load sample "${samplePath}" (ID: ${sampleId}):`, error.message)
        }
      }
    }

    // Prepare program A data
    const programAData: SingleProgramData = {
      bytecode: programA.bytecode || new Float64Array(0),
      sequences: (programA.sequences || []).map((seq, index) => ({
        data: this.serializeSequence(seq),
        slotIndex: index,
      })),
      samples: samplesData,
      impulseResponses: impulseResponsesData,
      bpm: programA.bpm,
      hasExplicitBpm: programA.hasExplicitBpm,
      tune: this.tune,
    }

    // Prepare program B data
    const programBData: SingleProgramData = {
      bytecode: programB.bytecode || new Float64Array(0),
      sequences: (programB.sequences || []).map((seq, index) => ({
        data: this.serializeSequence(seq),
        slotIndex: 32 + index,
      })),
      samples: samplesData,
      impulseResponses: impulseResponsesData,
      bpm: programB.bpm,
      hasExplicitBpm: programB.hasExplicitBpm,
      tune: this.tune,
    }

    // Prepare dual program data
    const dualProgramData: DualProgramData = {
      programA: programAData,
      programB: programBData,
      globalBpm,
      crossfade,
      tune: this.tune,
    }

    // Send to worklet
    if (resetState || this.isFirstLoad) {
      await this.workletRpc!.loadNewProgram(dualProgramData)
      this.isFirstLoad = false
    }
    else {
      await this.workletRpc!.loadProgram(dualProgramData)
    }

    // Update control buffer
    this.bufferManager.setGlobalBpm(globalBpm)
    this.bufferManager.setCrossfade(crossfade)
    this.bufferManager.setTune(this.tune)

    console.log('✅ Dual program loaded')
  }

  async loadProgram(source: string): Promise<void> {
    await this.ensureInitialized()

    if (!this.workletRpc || !this.irLoader || !this.sampleLoader || !this.samLoader) {
      throw new Error('loopmaster not initialized. Call init() first.')
    }

    console.log('[dspscript] Loading single mode program')

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Loading timeout - operation took too long')), 15000)
    })

    return Promise.race([
      this.loadProgramInternal(source),
      timeoutPromise,
    ])
  }

  async loadNewProgram(source: string): Promise<void> {
    await this.ensureInitialized()

    if (!this.workletRpc || !this.irLoader || !this.sampleLoader || !this.samLoader) {
      throw new Error('loopmaster not initialized. Call init() first.')
    }

    console.log('[dspscript] Loading new single mode program (with reset)')

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Loading timeout - operation took too long')), 15000)
    })

    return Promise.race([
      this.loadProgramInternal(source, true),
      timeoutPromise,
    ])
  }

  private async loadProgramInternal(source: string, resetState: boolean = false): Promise<void> {
    // Compile program - use global BPM from control buffer (not locked program BPM)
    const globalBpm = this.bufferManager.getGlobalBpm()
    this.globalBpm = globalBpm // Keep in sync
    const program = this.compiler.compile(source, globalBpm)

    // Load impulse responses
    const impulseResponsesData: IRData[] = []
    for (const irPath of program.impulseResponses) {
      try {
        const irData = await this.withTimeout(
          this.irLoader!.loadImpulseResponse(irPath),
          5000,
          `IR loading timeout: ${irPath}`,
        )
        const channelData = irData.audioBuffer.getChannelData(0)
        const buffer = new Float32Array(channelData.length)
        buffer.set(channelData)
        impulseResponsesData.push({
          irId: irData.id,
          data: buffer.buffer,
          length: channelData.length,
        })
      }
      catch (error: any) {
        console.error(`Failed to load IR "${irPath}":`, error.message)
      }
    }

    // Load samples
    const samplesData: SampleData[] = []
    for (let sampleId = 0; sampleId < program.samplePaths.length; sampleId++) {
      const samplePath = program.samplePaths[sampleId]
      if (samplePath.startsWith('__SAM__')) {
        const samHashStr = samplePath.replace('__SAM__', '')
        const samHash = parseInt(samHashStr, 10)
        const params = program.samSpeeches.find(([hash]) => hash === samHash)?.[1]
        if (params) {
          try {
            const samData = await this.withTimeout(
              this.samLoader!.generateSpeech(params),
              5000,
              `SAM speech generation timeout: "${params.speak}"`,
            )
            const channelData = samData.audioBuffer.getChannelData(0)
            const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
            const buffer = channelData.slice(0, length)
            samplesData.push({
              sampleId,
              data: buffer.buffer,
              length,
              contentHash: samData.id,
            })
          }
          catch (error: any) {
            console.error(`Failed to generate SAM speech "${params.speak}":`, error.message)
          }
        }
      }
      else {
        try {
          const sampleData = await this.withTimeout(
            this.sampleLoader!.loadSample(samplePath),
            5000,
            `Sample loading timeout: ${samplePath}`,
          )
          const channelData = sampleData.audioBuffer.getChannelData(0)
          const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
          const buffer = channelData.slice(0, length)
          const contentHash = hashFloat32Array(buffer)
          samplesData.push({
            sampleId,
            data: buffer.buffer,
            length,
            contentHash,
          })
        }
        catch (error: any) {
          console.error(`Failed to load sample "${samplePath}" (ID: ${sampleId}):`, error.message)
        }
      }
    }

    // Prepare program data
    const programData: SingleProgramData = {
      bytecode: program.bytecode || new Float64Array(0),
      sequences: (program.sequences || []).map((seq, index) => ({
        data: this.serializeSequence(seq),
        slotIndex: index,
      })),
      samples: samplesData,
      impulseResponses: impulseResponsesData,
      bpm: program.bpm,
      hasExplicitBpm: program.hasExplicitBpm,
      tune: this.tune,
    }

    // Send to worklet
    if (resetState || this.isFirstLoad) {
      await this.workletRpc!.loadNewProgram(programData)
      this.isFirstLoad = false
    }
    else {
      await this.workletRpc!.loadProgram(programData)
    }

    // Update tune in control buffer
    // Note: BPM is already set by worklet's loadProgram(), don't call setGlobalBpm here
    this.bufferManager.setTune(this.tune)

    console.log('✅ Program loaded')
  }

  async start() {
    await this.ensureInitialized()
    if (!this.audioContext) {
      throw new Error('loopmaster not initialized. Call init() first.')
    }

    // Safari requires explicit state handling - ensure context is running
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    // Wait for context to actually be running (Safari needs this)
    if (this.audioContext.state !== 'running') {
      await new Promise<void>((resolve, reject) => {
        const audioContext = this.audioContext
        if (!audioContext) {
          reject(new Error('AudioContext is null'))
          return
        }

        const timeout = setTimeout(() => {
          reject(new Error('AudioContext failed to start'))
        }, 1000)

        const onStateChange = () => {
          if (audioContext.state === 'running') {
            audioContext.removeEventListener('statechange', onStateChange)
            clearTimeout(timeout)
            resolve()
          }
        }

        audioContext.addEventListener('statechange', onStateChange)

        // Try resume again in case it didn't work
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(reject)
        }
      })
    }

    this.bufferManager.start()

    if (this.workletRpc) {
      await this.workletRpc.start()
    }
  }

  async stop() {
    if (!this.audioContext) {
      return
    }

    this.bufferManager.stop()

    if (this.workletRpc) {
      await this.workletRpc.stop()
    }
  }

  getAudioContext(): AudioContext | null {
    return this.audioContext
  }

  isPlaying(): boolean {
    return this.bufferManager.isPlaying()
  }

  setSyncMode(bars: number): void {
    this.bufferManager.setSyncMode(bars)
  }

  getSyncMode(): number {
    return this.bufferManager.getSyncMode()
  }

  getWorkletNode(): AudioWorkletNode | null {
    return this.workletNode
  }

  async getSequencerPointers(): Promise<Array<{ seqId: number; statePtr: number; sequenceId: number }>> {
    if (!this.initialized || !this.workletRpc) {
      return []
    }
    return this.workletRpc.getSequencerPointers()
  }

  async getWaveformPointers(): Promise<Array<{ waveformId: number; bufferPtr: number; writePosition: number }>> {
    if (!this.initialized || !this.workletRpc) {
      return []
    }
    return this.workletRpc.getWaveformPointers()
  }

  getWasmMemory(): SharedArrayBuffer | null {
    return this.wasmMemory
  }

  getMasterGain(): GainNode | null {
    return this.masterGain
  }

  setMasterVolume(value: number): void {
    this.masterVolume = value
    if (this.masterGain) this.masterGain.gain.value = value
  }

  getMasterVolume(): number {
    return this.masterVolume
  }

  setCrossfade(value: number): void {
    this.crossfade = value
    this.bufferManager.setCrossfade(value)
  }

  setGlobalBpm(value: number): void {
    this.globalBpm = value
    this.bufferManager.setGlobalBpm(value)
  }

  /**
   * Get the current BPM from WASM (respects program BPM if set, otherwise global BPM)
   * Reads directly from control buffer to get the most up-to-date value
   */
  getCurrentBpm(): number {
    // Read BPM directly from control buffer (written by worklet during program load)
    const bpm = this.bufferManager.getBpm()
    return bpm
  }

  setTune(value: number): void {
    this.tune = value
    this.bufferManager.setTune(value)
  }

  getTune(): number {
    return this.tune
  }

  async setOversampling(value: 1 | 2 | 4 | 8): Promise<void> {
    this.oversampling = value
    this.bufferManager.setOversampling(value)

    // Update WASM if initialized
    if (this.workletNode) {
      // Wait a bit for the control buffer to be updated
      await new Promise(resolve => setTimeout(resolve, 10))
    }
  }

  getOversampling(): 1 | 2 | 4 | 8 {
    return this.oversampling
  }

  getGlobalSampleCounter(): number {
    const outputLatencySamples = Math.floor(
      (this.audioContext?.outputLatency ?? 0)
        * (this.audioContext?.sampleRate ?? 48000),
    )
    return Math.max(0, this.bufferManager.getGlobalSampleCounter() - outputLatencySamples)
  }

  getRingAnalyser(): RingAnalyser | null {
    return this.ringAnalyser
  }

  getWaveformWritePositions(): Int32Array | null {
    const sharedBuffers = this.bufferManager.getSharedBuffers()
    return new Int32Array(sharedBuffers.waveformWritePositions)
  }

  dispose(): void {
    this.stop()

    if (this.workletNode) {
      try {
        this.workletNode.disconnect()
      }
      catch (e) {
        // Ignore errors if already disconnected
      }
      this.workletNode = null
    }

    if (this.masterGain) {
      try {
        this.masterGain.disconnect()
      }
      catch (e) {
        // Ignore errors if already disconnected
      }
      this.masterGain = null
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {
        // Ignore errors on close
      })
      this.audioContext = null
    }

    this.irLoader = null
    this.sampleLoader = null
    this.samLoader = null
    this.initialized = false
  }
}

// Create a global instance
export const dspscript = new loopmaster()

// Dispose on HMR
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    dspscript.dispose()
  })
}
